'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector, store, type AppDispatch } from '@/store/store';
import { workflowApi } from '@/store/api/workflowApi';
import { phoneWorkflowApi } from '@/store/api/phoneWorkflowApi';
import {
  bulkBatchesHydrated,
  bulkBatchesSwept,
  selectRunningBulkBatchCount,
  type BulkBatchesState,
} from '@/store/slices/bulkBatchesSlice';
import { notifyBulkBatchCompleted } from '@/lib/utils/bulk-batch-completion.utils';

/**
 * Backstop poll, and ONLY while a batch is actually running. The
 * `bulk.progress` SSE event is the primary signal and fires every 5
 * completions; this covers events dropped while the stream was reconnecting,
 * and a batch that finished while this tab was closed.
 *
 * Two requests per tick regardless of how many batches or contacts are
 * running - the whole point of the bulk endpoints is that cost doesn't scale
 * with rows.
 */
const BATCH_POLL_MS = 20_000;

/** How often finished cards are swept once their grace period is up. */
const SWEEP_MS = 10_000;

/** Guards against two refreshes racing into the same prune - module-level, since the hook is mounted in one place but `refreshBulkBatches` can also be called from the panel's refresh button. */
let refreshInFlight = false;

/**
 * Fetches both list endpoints and reconciles them into bulkBatchesSlice.
 *
 * A plain function rather than part of the hook so the notification panel's
 * refresh button can trigger one without mounting a second poller.
 *
 * `requestedAt` is captured before the fetch and handed to the reducer, and
 * that ordering is load-bearing: the list contains unfinished batches only, so
 * a tracked batch missing from it has normally finished - but a batch created
 * AFTER the request went out would also be missing, and marking that one done
 * would freeze a card at 0% for a run that is only just starting. See
 * bulkBatchesHydrated.
 */
export async function refreshBulkBatches(dispatch: AppDispatch): Promise<void> {
  if (refreshInFlight) return;
  refreshInFlight = true;
  const requestedAt = Date.now();

  try {
    // Settled, not all: the two categories are independent, and a phone
    // outage must not also blank the email list.
    const [emailResult, phoneResult] = await Promise.allSettled([
      dispatch(
        workflowApi.endpoints.listBulkWorkflows.initiate(undefined, { forceRefetch: true })
      ).unwrap(),
      dispatch(
        phoneWorkflowApi.endpoints.listBulkPhoneWorkflows.initiate(undefined, { forceRefetch: true })
      ).unwrap(),
    ]);

    if (emailResult.status === 'fulfilled') {
      dispatch(
        bulkBatchesHydrated({ category: 'email', batches: emailResult.value ?? [], requestedAt })
      );
    }
    if (phoneResult.status === 'fulfilled') {
      dispatch(
        bulkBatchesHydrated({ category: 'phone', batches: phoneResult.value ?? [], requestedAt })
      );
    }

    // Second completion signal, covering a final `bulk.progress` that never
    // arrived (dropped push, stream reconnecting, or the batch finishing while
    // this tab was closed). The reducers above mark a batch done when it drops
    // out of the list; this turns that into the same contacts refresh the
    // event path does. Idempotent per batch, so whichever signal lands first
    // wins and the other is a no-op.
    const byKey = (store.getState() as { bulkBatches: BulkBatchesState }).bulkBatches.byKey;
    for (const key of Object.keys(byKey)) {
      const batch = byKey[key];
      if (batch.done) notifyBulkBatchCompleted(dispatch, batch, key);
    }
  } finally {
    refreshInFlight = false;
  }
}

/**
 * Keeps bulkBatchesSlice in sync with the two list endpoints.
 *
 * Mounted ONCE, in the app sidebar - not in the notification panel. The bell
 * has to show that a batch is running whether or not the panel has ever been
 * opened, and after a reload the slice starts empty, so something permanently
 * mounted has to do the initial fetch. A batch submitted in this tab is in the
 * slice immediately (bulkBatchRegistered) and one started elsewhere arrives
 * over SSE, so this only has to cover the reload case and dropped events.
 *
 * Cost when nothing is running: two requests per page load, then nothing -
 * polling starts only once a batch is actually in flight and stops when it
 * finishes.
 */
export function useBulkBatchTracking({ enabled = true }: { enabled?: boolean } = {}) {
  const dispatch = useAppDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const runningCount = useAppSelector(selectRunningBulkBatchCount);
  const hasFetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    await refreshBulkBatches(dispatch);
    setIsRefreshing(false);
  }, [dispatch]);

  // One fetch per mount, to discover batches already running before this tab
  // loaded. Ref-guarded so React's development double-invoke doesn't make it two.
  useEffect(() => {
    if (!enabled || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    void refresh();
  }, [enabled, refresh]);

  // Polling exists only while something is in flight. An idle session makes no
  // requests at all - a batch started in another tab still reaches this one
  // over SSE, so there's nothing to discover by polling an empty list.
  useEffect(() => {
    if (!enabled || runningCount === 0) return;
    const id = setInterval(() => void refreshBulkBatches(dispatch), BATCH_POLL_MS);
    return () => clearInterval(id);
  }, [enabled, runningCount, dispatch]);

  // Finished cards linger briefly so a run completing while the panel is open
  // doesn't just disappear mid-glance, then get swept.
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => dispatch(bulkBatchesSwept({ now: Date.now() })), SWEEP_MS);
    return () => clearInterval(id);
  }, [enabled, dispatch]);

  return { refresh, isRefreshing, runningCount };
}
